import { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { NAVY } from '../utils/constants';
import { useCruiseSettings } from '../hooks/useCruiseSettings';
import { Button, Field, NoteStrip } from './ui';
import { UserPlus, Check, RotateCcw, ChevronDown, ChevronRight, Pencil } from 'lucide-react';
import { CUSTOMER_WINDOW_DAYS } from '../utils/bookingWindow';

const STATUSES = ['New', 'Contacted', 'Handled', 'Closed'];
const OPEN_STATUSES = ['New', 'Contacted', 'Handled'];

// Closing needs a reason, so the closed list can be read later. 'date_passed'
// is set only by the daily sweep (Stage 2). 'duplicate' is set by the sweep
// when two enquiries match one booking (the earlier one is the conversion)
// and can also be picked by staff for a repeat enquiry.
const CLOSE_REASONS = [
  { value: 'booked_elsewhere', label: 'Booked elsewhere' },
  { value: 'not_coming',       label: 'Not coming' },
  { value: 'wrong_number',     label: 'Wrong number' },
  { value: 'no_answer',        label: 'No answer' },
  { value: 'duplicate',        label: 'Duplicate' },
  { value: 'date_passed',      label: 'Date passed' },
];
const STAFF_CLOSE_REASONS = CLOSE_REASONS.filter(r => r.value !== 'date_passed');
const reasonLabel = (v) => (CLOSE_REASONS.find(r => r.value === v) || {}).label || v || '—';
// Date-passed closes are the ones worth a second look (wrong date, changed
// plans), so they get the only coloured chip.
const reasonChipStyle = (v) => v === 'date_passed'
  ? { bg: '#FEF3C7', border: '#FCD34D', text: '#92400E' }
  : { bg: 'var(--color-sunken)', border: 'var(--color-border-strong)', text: 'var(--color-ink-muted)' };

// How long a closed lead stays in the "Recently closed" panel before it lives
// only under the Closed filter.
const RECENT_CLOSE_DAYS = 14;

const CRUISE_LABELS = { sunset: 'Sunset', sundown: 'Sundown', dinner: 'Dinner' };
const DECK_LABELS = { upper: 'Upper Deck', middle: 'Middle Deck', none: 'No Preference' };
const SOURCE_LABELS = { website: 'Website', whatsapp: 'WhatsApp', staff: 'Staff lead' };

// Convert-to-booking mapping. Enquiry data is rougher than lead data: the
// website sends constrained codes, but WhatsApp enquiries can carry free text
// ("Private charter & events") or no deck at all. Anything that doesn't map to
// a real cruise/deck prefills as BLANK — the Add Inquiry form shows its
// required "Select cruise…" placeholder and blocks save until the manager
// chooses — and the guest's raw ask is carried into notes so nothing is lost.
const mapCruiseName = (pref, settings) => {
  if (!pref) return '';
  const code = String(pref).trim().toLowerCase();
  const hit = settings.find(s => (s.cruise_name || '').toLowerCase().includes(code));
  return hit ? hit.cruise_name : '';
};
const DECK_MAP = { upper: 'Upper Deck', middle: 'Middle Deck' };

const STATUS_COLORS = {
  New:       { bg: '#FEF3C7', border: '#FCD34D', text: '#92400E' },
  Contacted: { bg: '#DBEAFE', border: '#93C5FD', text: '#1E40AF' },
  Handled:   { bg: '#DCFCE7', border: '#86EFAC', text: '#15803D' },
  Closed:    { bg: 'var(--color-sunken)', border: 'var(--color-border-strong)', text: 'var(--color-ink-muted)' },
};

// YYYY-MM-DD (date-only columns) → DD-MM-YYYY
const toDisplayDate = (val) => {
  if (!val) return '—';
  const [y, m, d] = val.slice(0, 10).split('-');
  return y && m && d ? `${d}-${m}-${y}` : val;
};

// Timestamptz → local DD-MM-YYYY (slicing the raw ISO string gives the UTC
// date — an enquiry received 00:00–05:29 IST would display as the previous day)
const tsToDisplayDate = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
};

const relTime = (iso) => {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return h === 1 ? '1 hour ago' : `${h} hours ago`;
  const d = Math.floor(h / 24);
  return d === 1 ? 'yesterday' : `${d} days ago`;
};

// Staff identity from an email — enough for "who closed this?".
const staffLabel = (email) => {
  if (!email) return '—';
  if (email === 'sweep') return 'Automatic';
  return email.split('@')[0] || email;
};

// preferred_date is text: YYYY-MM-DD by convention, but a WhatsApp guest can
// have typed anything ("next Saturday").
const isIsoDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v || ''));
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
// Online booking window: see src/utils/bookingWindow.js (mirror of the public
// repo's _shared/bookingWindow.ts). Used for the hint only; the gate is server-side.
const ONLINE_WINDOW_DAYS = CUSTOMER_WINDOW_DAYS;
const daysFromToday = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  const [ty, tm, td] = todayIso().split('-').map(Number);
  return Math.round((Date.UTC(y, m - 1, d) - Date.UTC(ty, tm - 1, td)) / 86400000);
};
const openingDateIso = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d - ONLINE_WINDOW_DAYS)).toISOString().slice(0, 10);
};

// A real date that is already behind us. The morning sweep closes any open
// lead in this state, so reopening one needs a new date.
const isPastDate = (v) => isIsoDate(v) && v < todayIso();

const SETUP_SQL_MSG = 'One-time database setup needed: paste db/enquiry_notes_and_date.sql into the Supabase SQL editor, then refresh this page.';
const DATE_CHANGE_ERRORS = {
  DATE_IN_PAST:      'That date has already passed.',
  INVALID_DATE:      'That is not a valid date.',
  ENQUIRY_CONVERTED: 'This enquiry is already converted — change the date on the booking instead.',
  ENQUIRY_NOT_FOUND: 'This enquiry no longer exists. Refresh the page.',
  INVALID_STATUS:    'That status cannot be used to reopen a lead.',
  NOT_STAFF:         'Your login is not allowed to change enquiries.',
};

const chip = (text, { bg, border, text: color }) => (
  <span style={{ display: 'inline-block', background: bg, color, border: `1px solid ${border}`, borderRadius: 6, padding: '2px 9px', fontSize: '0.68rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
    {text}
  </span>
);

// ─── Styles (mirrors RangeReports) ────────────────────────────────────────────
const card = { background: '#fff', border: '1px solid var(--color-border)', borderRadius: 14, padding: '18px 22px', marginBottom: 18, boxShadow: '0 1px 6px rgba(10,22,40,0.06)' };
const thStyle = { padding: '8px 12px', fontWeight: 700, fontSize: 'var(--text-xs)', color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'left', whiteSpace: 'nowrap', background: 'var(--color-sunken)', borderBottom: '1px solid var(--color-border-strong)' };
const tdStyle = { padding: '8px 12px', fontSize: 'var(--text-sm)', color: 'var(--color-ink-brand-softer)', borderBottom: '1px solid var(--color-border-subtle)', verticalAlign: 'middle' };
const tableWrap = { overflowX: 'auto', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)' };
const tblStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' };

const filterBtnStyle = (active) => ({
  padding: '7px 16px',
  background: active ? NAVY : 'var(--color-sunken)',
  color: active ? '#ffffff' : 'var(--color-ink-muted)',
  border: `1.5px solid ${active ? NAVY : 'var(--color-border-strong)'}`,
  borderRadius: 8,
  fontSize: '0.8rem',
  fontWeight: active ? 700 : 600,
  cursor: 'pointer',
  transition: 'background 0.12s, color 0.12s',
});

const outlineBtn = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 11px', borderRadius: 8, border: '1.5px solid var(--color-border-strong)', background: '#fff', color: NAVY, fontWeight: 700, fontSize: '0.74rem', cursor: 'pointer', whiteSpace: 'nowrap' };
const solidBtn = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 11px', borderRadius: 8, border: 'none', background: NAVY, color: '#fff', fontWeight: 700, fontSize: '0.74rem', cursor: 'pointer', whiteSpace: 'nowrap' };

// ─── Close-reason modal ───────────────────────────────────────────────────────

function CloseReasonModal({ row, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const confirm = async () => {
    if (!reason) { setError('Pick a reason.'); return; }
    setSaving(true);
    setError('');
    const err = await onConfirm(reason);
    setSaving(false);
    if (err) setError(err);
  };

  return (
    <div className="ss-modal-backdrop" onClick={onClose}>
      <div className="ss-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 800, color: NAVY, margin: '0 0 4px' }}>Close lead</h3>
        <div style={{ fontSize: '0.82rem', color: 'var(--color-ink-secondary)', marginBottom: 14 }}>
          {row.full_name}{row.phone ? ` · ${row.phone}` : ''}{row.preferred_date ? ` · ${toDisplayDate(row.preferred_date)}` : ''}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
          {STAFF_CLOSE_REASONS.map(r => (
            <button key={r.value} onClick={() => setReason(r.value)}
              style={{
                padding: '9px 10px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem',
                background: reason === r.value ? 'var(--color-sunken)' : '#fff',
                color: NAVY,
                border: `1.5px solid ${reason === r.value ? NAVY : 'var(--color-border-strong)'}`,
              }}>
              {r.label}
            </button>
          ))}
        </div>

        <p style={{ margin: 0, fontSize: '0.72rem', color: 'var(--color-ink-muted)' }}>
          Closed leads stay under "Recently closed" for {RECENT_CLOSE_DAYS} days, then under the Closed filter.
          A closed lead can be reopened or converted at any time.
        </p>

        {error && <p style={{ margin: '10px 0 0', fontSize: '0.78rem', color: '#991B1B' }}>{error}</p>}

        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} disabled={saving}
            style={{ padding: '8px 18px', background: 'var(--color-sunken)', color: NAVY, border: '1.5px solid var(--color-border-strong)', borderRadius: 8, fontWeight: 700, fontSize: '0.84rem', cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={confirm} disabled={saving}
            style={{ padding: '8px 18px', background: NAVY, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.84rem', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Closing…' : 'Close lead'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Edit modal (preferred date + note) ───────────────────────────────────────
// The note is one editable field. The date goes through change_enquiry_date()
// (db/enquiry_notes_and_date.sql), which decides in the database — from the
// row as it is at that moment — whether to clear the date-open stamp and
// whether a "Date passed" close reopens. This screen stays open all day, so
// the dialog also re-reads the row when it opens rather than trusting the
// list's copy.
//
// `reopenTo` (a status) turns it into the Reopen dialog: reopening a closed
// lead whose date has passed needs a new date, or the morning sweep closes it
// again. The date starts empty and the save reopens to that status.

function EditEnquiryModal({ row, reopenTo, onClose, onSaved }) {
  // A past date is not offered back when reopening — the point is a new one.
  const startDate = (r) => {
    if (!isIsoDate(r.preferred_date)) return '';
    return reopenTo && r.status === 'Closed' && isPastDate(r.preferred_date) ? '' : r.preferred_date;
  };
  const [fresh, setFresh]     = useState(row);
  const [loading, setLoading] = useState(true);
  const [date, setDate]       = useState(startDate(row));
  const [note, setNote]       = useState(row.note || '');
  const [saving, setSaving]   = useState(false);
  const [tried, setTried]     = useState(false);   // Save pressed at least once
  const [error, setError]     = useState('');
  const savedSomething = useRef(false);

  useEffect(() => {
    let live = true;
    supabase.from('online_enquiries').select('*').eq('id', row.id).single().then(({ data }) => {
      if (!live) return;
      if (data) {
        setFresh(data);
        setDate(startDate(data));
        setNote(data.note || '');
      }
      setLoading(false);
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.id]);

  const converted   = !!fresh.converted_booking_id;
  // Someone else may have reopened it since the list loaded; then this is a plain edit.
  const reopening   = !!reopenTo && fresh.status === 'Closed' && !converted;
  const currentDate = isIsoDate(fresh.preferred_date) ? fresh.preferred_date : '';
  const dateChanged = !converted && date !== currentDate;
  const noteChanged = note.trim() !== (fresh.note || '').trim();
  const days = isIsoDate(date) ? daysFromToday(date) : null;
  const dateProblem = !(dateChanged || reopening) ? '' :
    !isIsoDate(date) ? (reopening ? 'Choose the new date.' : 'Choose a date.') :
    days < 0 ? 'That date has already passed.' : '';

  // What saving will do, said before it happens.
  const effects = [];
  if (reopening && !dateProblem) {
    effects.push(`Saving reopens this lead as ${reopenTo}.`);
  }
  if (dateChanged && !dateProblem) {
    if (!reopening && fresh.status === 'Closed') {
      effects.push(fresh.close_reason === 'date_passed'
        ? 'This lead was closed as Date passed. Saving reopens it as Contacted.'
        : `This lead stays closed (${reasonLabel(fresh.close_reason)}). Use Reopen if the guest is coming after all.`);
    }
    if (fresh.date_open_sent_at) {
      effects.push(
        `The "date is open" message already went out for ${toDisplayDate(fresh.preferred_date)}. ` +
        (days <= ONLINE_WINDOW_DAYS
          ? 'Online booking for the new date is already open, so a new message goes out with the next morning run.'
          : `A new message goes out when booking for the new date opens on ${toDisplayDate(openingDateIso(date))}.`)
      );
    }
  }

  const close = () => (savedSomething.current ? onSaved() : onClose());

  const save = async () => {
    setTried(true);
    if (dateProblem) return;   // shown under the date field
    if (!dateChanged && !reopening && !noteChanged) { close(); return; }
    setSaving(true);
    setError('');
    let current = fresh;

    if (dateChanged || reopening) {
      const { data, error: rpcErr } = await supabase.rpc('change_enquiry_date', {
        p_enquiry_id: fresh.id,
        p_new_date: date,
        p_reopen_status: reopening ? reopenTo : null,
      });
      if (rpcErr) {
        setSaving(false);
        const missing = rpcErr.code === 'PGRST202' || rpcErr.code === '42883';
        const known = Object.keys(DATE_CHANGE_ERRORS).find(k => (rpcErr.message || '').includes(k));
        setError(missing ? SETUP_SQL_MSG : known ? DATE_CHANGE_ERRORS[known] : `Could not change the date: ${rpcErr.message}`);
        return;
      }
      savedSomething.current = true;
      if (data) { current = data; setFresh(data); }
    }

    if (noteChanged) {
      const { data: userData } = await supabase.auth.getUser();
      const { data, error: noteErr } = await supabase.from('online_enquiries')
        .update({
          note: note.trim() || null,
          note_updated_by: userData?.user?.email || 'unknown',
          note_updated_at: new Date().toISOString(),
        })
        .eq('id', current.id)
        .select()
        .single();
      if (noteErr) {
        setSaving(false);
        const dateSaved = (dateChanged || reopening) ? `The ${reopening ? 'lead was reopened' : 'date was saved'}, but the note was not. ` : '';
        setError(dateSaved + (/column|schema cache/i.test(noteErr.message || '') ? SETUP_SQL_MSG : `Could not save the note: ${noteErr.message}`));
        return;
      }
      savedSomething.current = true;
      if (data) setFresh(data);
    }

    setSaving(false);
    onSaved();
  };

  const rawDate = fresh.preferred_date && !isIsoDate(fresh.preferred_date) ? fresh.preferred_date : '';
  const dateHint =
    converted ? 'Converted to a booking — change the date on the booking instead.' :
    reopening && isPastDate(fresh.preferred_date) ? `They wanted ${toDisplayDate(fresh.preferred_date)}, which has passed. A lead with a past date is closed again by the next morning sweep, so reopening needs the new date.` :
    rawDate ? `The guest wrote "${rawDate}". Pick the actual date to put this lead on the calendar.` :
    '';

  return (
    <div className="ss-modal-backdrop" onClick={saving ? undefined : close}>
      <div className="ss-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
        <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 700, color: 'var(--color-ink-brand)', margin: '0 0 4px' }}>{reopening ? 'Reopen lead' : 'Edit enquiry'}</h3>
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-ink-secondary)', marginBottom: 14 }}>
          {fresh.full_name}{fresh.phone ? ` · ${fresh.phone}` : ''}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label={reopening ? 'New preferred date' : 'Preferred date'} htmlFor="enq-edit-date" hint={dateHint} error={date || tried ? dateProblem : ''}>
            <input
              id="enq-edit-date"
              type="date"
              value={date}
              min={todayIso()}
              disabled={loading || saving || converted}
              onChange={e => { setDate(e.target.value); setError(''); }}
              onClick={e => e.target.showPicker && e.target.showPicker()}
              style={{ colorScheme: 'light' }}
            />
          </Field>

          {effects.length > 0 && (
            <div style={{ background: 'var(--color-warning-bg)', border: '1px solid var(--color-warning-border)', borderRadius: 'var(--radius-sm)', padding: '8px 12px', fontSize: 'var(--text-xs)', color: 'var(--color-warning-strong)', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {effects.map(t => <div key={t}>{t}</div>)}
            </div>
          )}

          <Field label="Note" htmlFor="enq-edit-note" hint="Shown on the list for everyone. Editing replaces the note — keep what is still true.">
            <textarea
              id="enq-edit-note"
              value={note}
              rows={3}
              disabled={loading || saving}
              onChange={e => { setNote(e.target.value); setError(''); }}
              placeholder="What they said — occasion, flexibility on date, when to call…"
            />
          </Field>
        </div>

        {error && <p role="alert" style={{ margin: '12px 0 0', fontSize: 'var(--text-xs)', color: 'var(--color-danger-strong)' }}>{error}</p>}

        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" size="sm" onClick={close} disabled={saving}>Cancel</Button>
          <Button size="sm" onClick={save} disabled={loading || saving}>
            {saving ? 'Saving…' : loading ? 'Loading…' : reopening ? 'Reopen' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Screen ───────────────────────────────────────────────────────────────────

export default function Enquiries({ onNavigate }) {
  const { settings: cruiseSettings } = useCruiseSettings();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState('open'); // 'open' | 'closed' | 'all'
  const [dateFilter, setDateFilter] = useState(''); // ISO YYYY-MM-DD; empty = all dates
  const [error, setError]         = useState('');
  const [closing, setClosing]     = useState(null);  // row being closed (reason modal)
  const [editing, setEditing]     = useState(null);  // { row, reopenTo? } in the edit / reopen dialog
  const [dateChanges, setDateChanges] = useState({}); // enquiry id → latest preferred-date change
  const [showRecent, setShowRecent] = useState(false);

  const load = useCallback(async () => {
    const { data, error: fetchErr } = await supabase
      .from('online_enquiries').select('*').order('created_at', { ascending: false });
    if (fetchErr) setError('Failed to load enquiries.');
    else setEnquiries(data || []);
    setLoading(false);
    // Date-change history, for the "was …" line under a changed date. The
    // table arrives with db/enquiry_notes_and_date.sql; until then (or on any
    // error) the list simply shows no history.
    const { data: changes } = await supabase
      .from('enquiry_date_changes')
      .select('enquiry_id, old_date, new_date, changed_by, changed_at')
      .order('changed_at', { ascending: false });
    const latest = {};
    (changes || []).forEach(c => { if (!latest[c.enquiry_id]) latest[c.enquiry_id] = c; });
    setDateChanges(latest);
  }, []);

  useEffect(() => { load(); }, [load]);

  const patchLocal = (id, patch) =>
    setEnquiries(list => list.map(e => e.id === id ? { ...e, ...patch } : e));

  const columnsMissing = (err) => /column|schema cache/i.test(err?.message || '');

  // Status dropdown. Closed is not set directly: it opens the reason modal.
  // Leaving Closed for any other status is a reopen and clears the close fields.
  const handleStatusChange = async (row, newStatus) => {
    setError('');
    if (newStatus === 'Closed') { setClosing(row); return; }
    // Reopening with a date that has passed undoes itself: the morning sweep
    // closes the lead again. Ask for the new date instead (the dialog reopens
    // and re-dates in one step). Converted leads are never swept.
    if (row.status === 'Closed' && isPastDate(row.preferred_date) && !row.converted_booking_id) {
      setEditing({ row, reopenTo: newStatus });
      return;
    }
    const prev = { status: row.status, close_reason: row.close_reason, closed_by: row.closed_by, closed_at: row.closed_at };
    const patch = row.status === 'Closed'
      ? { status: newStatus, close_reason: null, closed_by: null, closed_at: null }
      : { status: newStatus };
    patchLocal(row.id, patch);
    const { error: updateErr } = await supabase.from('online_enquiries').update(patch).eq('id', row.id);
    if (updateErr) {
      patchLocal(row.id, prev);
      setError(columnsMissing(updateErr)
        ? 'The database is missing the lead columns — paste the Stage 1 migration in Supabase first.'
        : `Could not update status for ${row.full_name}. Please try again.`);
    }
  };

  const closeWithReason = async (row, reason) => {
    const { data: userData } = await supabase.auth.getUser();
    const patch = {
      status: 'Closed',
      close_reason: reason,
      closed_by: userData?.user?.email || 'unknown',
      closed_at: new Date().toISOString(),
    };
    const { error: updateErr } = await supabase.from('online_enquiries').update(patch).eq('id', row.id);
    if (updateErr) {
      return columnsMissing(updateErr)
        ? 'The database is missing the lead columns — paste the Stage 1 migration in Supabase first.'
        : updateErr.message;
    }
    patchLocal(row.id, patch);
    setClosing(null);
    return '';
  };

  const reopen = (row) => handleStatusChange(row, 'Contacted');

  const convertEnquiry = (row) => {
    const cruiseName = mapCruiseName(row.cruise_preference, cruiseSettings);
    const deckName = DECK_MAP[String(row.deck_preference || '').trim().toLowerCase()] || '';
    // Preserve the guest's actual ask when it didn't map to a real option.
    const askedBits = [];
    if (row.cruise_preference && !cruiseName) askedBits.push(`asked for: '${row.cruise_preference}'`);
    if (row.deck_preference && !deckName) askedBits.push(`deck: ${DECK_LABELS[row.deck_preference] || row.deck_preference}`);
    if (row.note) askedBits.push(row.note);
    onNavigate('add-inquiry-prefill', row.preferred_date ? toDisplayDate(row.preferred_date) : '', {
      booking_name: row.full_name || '',
      guest_phone: (row.phone || '').replace(/\D/g, '').slice(-10),
      adults: row.adults ?? '',
      children: row.children ?? 0,
      infants: row.infants ?? 0,
      cruise_type: cruiseName,
      deck: deckName,
      notes: ['From enquiry', ...askedBits].join(' — '),
      // Same linkage the Incomplete Bookings convert writes: ties the booking
      // to the online customer so the two conversion paths stay one path.
      customer_id: row.customer_id || null,
      _enquiry_id: row.id,
      _return_to: 'enquiries',
    });
  };

  // A lead that became a booking is finished: it leaves the Open list and
  // lives under Converted (and All), whatever its status says.
  const isOpen = (e) => OPEN_STATUSES.includes(e.status) && !e.converted_booking_id;
  const openCount = enquiries.filter(isOpen).length;
  const visible = enquiries.filter(e => {
    const statusOk =
      filter === 'all' ? true :
      filter === 'closed' ? e.status === 'Closed' :
      filter === 'converted' ? !!e.converted_booking_id :
      isOpen(e);
    const dateOk = !dateFilter || e.preferred_date === dateFilter;
    return statusOk && dateOk;
  });

  // Closed in the last RECENT_CLOSE_DAYS days, date-passed first, newest
  // first within that. Rows closed before Stage 1 have no closed_at and
  // appear only under the Closed filter.
  const recentCutoff = Date.now() - RECENT_CLOSE_DAYS * 86400000;
  const recentlyClosed = enquiries
    .filter(e => e.status === 'Closed' && e.closed_at && new Date(e.closed_at).getTime() >= recentCutoff)
    .sort((a, b) => {
      const ap = a.close_reason === 'date_passed' ? 0 : 1;
      const bp = b.close_reason === 'date_passed' ? 0 : 1;
      return ap - bp || new Date(b.closed_at) - new Date(a.closed_at);
    });

  const showClosedCols = filter !== 'open';

  const emptyMessage = (
    filter === 'open' ? 'No open enquiries' :
    filter === 'closed' ? 'No closed enquiries' :
    filter === 'converted' ? 'No converted enquiries' :
    'No enquiries yet'
  ) + (dateFilter ? ' for this date.' : '.');

  const sourceLine = (row) => {
    const src = SOURCE_LABELS[row.source] || row.source || '';
    const by = row.source === 'staff' && row.added_by ? ` · ${staffLabel(row.added_by)}` : '';
    return src ? `${src}${by}` : '';
  };

  // The note under a row, highlighted. With a strip below, the row's divider
  // moves to the strip's row.
  const cellFor = (row) => ((row.note || '').trim() ? { ...tdStyle, borderBottom: 'none' } : tdStyle);
  const noteRow = (row, colSpan) => {
    const text = (row.note || '').trim();
    if (!text) return null;
    // Before the first edit, the note on a staff lead was written when the
    // lead was added; website/WhatsApp rows have no author to show.
    const who = row.note_updated_by || row.added_by;
    const when = row.note_updated_at || (row.added_by ? row.created_at : null);
    return (
      <tr>
        <td colSpan={colSpan} style={{ padding: '0 12px 9px', borderBottom: tdStyle.borderBottom }}>
          <NoteStrip lines={[{
            key: 'note',
            label: 'Note',
            meta: [who && staffLabel(who), when && relTime(when)].filter(Boolean).join(' · '),
            title: when ? new Date(when).toLocaleString('en-IN') : '',
            text,
          }]} />
        </td>
      </tr>
    );
  };

  const dateCell = (row) => {
    const change = dateChanges[row.id];
    return (
      <>
        {toDisplayDate(row.preferred_date)}
        {change && (
          <div style={{ fontSize: '0.7rem', color: 'var(--color-ink-muted)', marginTop: 2 }}
            title={`Changed by ${staffLabel(change.changed_by)} · ${new Date(change.changed_at).toLocaleString('en-IN')}`}>
            was {toDisplayDate(change.old_date)}
          </div>
        )}
      </>
    );
  };

  const renderActions = (row) => (
    <>
      <button onClick={() => { setError(''); setEditing({ row }); }} style={{ ...outlineBtn, marginRight: 6 }} title="Change the preferred date or the note">
        <Pencil size={13} strokeWidth={1.75} aria-hidden="true" />Edit
      </button>
      {row.status === 'Closed' && (
        <button onClick={() => reopen(row)} style={{ ...outlineBtn, marginRight: 6 }} title={isPastDate(row.preferred_date) && !row.converted_booking_id ? 'Their date has passed — reopening asks for the new date' : 'Back to Contacted'}>
          <RotateCcw size={13} strokeWidth={1.75} aria-hidden="true" />Reopen
        </button>
      )}
      {row.converted_booking_id ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#DCFCE7', color: '#15803D', border: '1px solid #86EFAC', borderRadius: 6, padding: '4px 10px', fontSize: '0.72rem', fontWeight: 700 }}>
          <Check size={12} strokeWidth={2} aria-hidden="true" />Converted
        </span>
      ) : (
        <button onClick={() => convertEnquiry(row)} style={solidBtn}>
          <UserPlus size={13} strokeWidth={1.75} aria-hidden="true" />Convert
        </button>
      )}
    </>
  );

  return (
    <div>
      <div className="back-row">
        <button className="back-btn" onClick={() => onNavigate('operations')}>← Operations</button>
      </div>

      <h2 style={{ fontSize: '1.15rem', fontWeight: 800, color: NAVY, marginBottom: 4 }}>Enquiries</h2>
      <p style={{ fontSize: '0.8rem', color: 'var(--color-ink-muted)', marginBottom: 20 }}>
        Leads from the website, WhatsApp and staff calls. {loading ? '' : `${openCount} open.`}
      </p>

      <div style={card}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
          {[{ key: 'open', label: 'Open' }, { key: 'closed', label: 'Closed' }, { key: 'converted', label: 'Converted' }, { key: 'all', label: 'All' }].map(f => (
            <button key={f.key} style={filterBtnStyle(filter === f.key)} onClick={() => setFilter(f.key)}>
              {f.label}
            </button>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--color-ink-muted)' }}>Preferred date</span>
            <input
              type="date"
              value={dateFilter}
              onChange={e => setDateFilter(e.target.value)}
              onClick={e => e.target.showPicker && e.target.showPicker()}
              style={{ background: 'var(--color-sunken)', border: '1.5px solid var(--color-border-strong)', borderRadius: 8, padding: '7px 10px', color: NAVY, fontSize: '0.82rem', fontWeight: 600, outline: 'none', colorScheme: 'light', cursor: 'pointer' }}
            />
            {dateFilter && (
              <button
                onClick={() => setDateFilter('')}
                style={{ padding: '7px 12px', background: 'transparent', color: 'var(--color-ink-muted)', border: '1.5px solid var(--color-border-strong)', borderRadius: 8, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {error && (
          <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '8px 12px', fontSize: '0.78rem', color: '#dc2626', marginBottom: 12 }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--color-ink-faint)', fontSize: '0.9rem' }}>Loading…</div>
        ) : visible.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--color-ink-faint)', fontSize: '0.9rem' }}>{emptyMessage}</div>
        ) : (
          <div style={tableWrap}>
            <table style={tblStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Phone</th>
                  <th style={thStyle}>Preferred Date</th>
                  <th style={thStyle}>Cruise</th>
                  <th style={thStyle}>Deck</th>
                  <th style={thStyle}>Pax</th>
                  <th style={thStyle}>Status</th>
                  {showClosedCols && <th style={thStyle}>Closed</th>}
                  <th style={thStyle}>Submitted</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(row => {
                  const clr = STATUS_COLORS[row.status] || STATUS_COLORS.Closed;
                  const td = cellFor(row);
                  return (
                    <Fragment key={row.id}>
                    <tr>
                      <td style={{ ...td, fontWeight: 600, color: NAVY }}>
                        {row.full_name}
                        {sourceLine(row) && (
                          <div style={{ fontSize: '0.7rem', fontWeight: 500, color: 'var(--color-ink-muted)', marginTop: 2 }}>
                            {sourceLine(row)}
                          </div>
                        )}
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{row.phone || '—'}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{dateCell(row)}</td>
                      <td style={td}>{CRUISE_LABELS[row.cruise_preference] || row.cruise_preference || '—'}</td>
                      <td style={td}>{DECK_LABELS[row.deck_preference] || row.deck_preference || '—'}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                        {`${Number(row.adults) || 0}A · ${Number(row.children) || 0}C · ${Number(row.infants) || 0}I`}
                      </td>
                      <td style={td}>
                        <select
                          value={row.status}
                          onChange={e => handleStatusChange(row, e.target.value)}
                          style={{ background: clr.bg, border: `1.5px solid ${clr.border}`, borderRadius: 8, padding: '5px 8px', color: clr.text, fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', outline: 'none' }}
                        >
                          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      {showClosedCols && (
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>
                          {row.status === 'Closed' ? (
                            <>
                              {chip(reasonLabel(row.close_reason), reasonChipStyle(row.close_reason))}
                              {row.closed_at && (
                                <div style={{ fontSize: '0.7rem', color: 'var(--color-ink-muted)', marginTop: 3 }} title={new Date(row.closed_at).toLocaleString('en-IN')}>
                                  {staffLabel(row.closed_by)} · {relTime(row.closed_at)}
                                </div>
                              )}
                            </>
                          ) : <span style={{ color: 'var(--color-ink-faint)' }}>—</span>}
                        </td>
                      )}
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{tsToDisplayDate(row.created_at)}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{renderActions(row)}</td>
                    </tr>
                    {noteRow(row, showClosedCols ? 10 : 9)}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {filter === 'open' && !loading && recentlyClosed.length > 0 && (
        <div style={{ marginBottom: 18 }}>
          <button
            onClick={() => setShowRecent(v => !v)}
            aria-expanded={showRecent}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--color-border)', background: '#fff', color: 'var(--color-ink-secondary)', fontWeight: 600, fontSize: '0.74rem', cursor: 'pointer' }}>
            {showRecent
              ? <ChevronDown size={13} strokeWidth={1.75} aria-hidden="true" />
              : <ChevronRight size={13} strokeWidth={1.75} aria-hidden="true" />}
            Recently closed ({recentlyClosed.length})
          </button>
          {showRecent && (
            <div style={{ marginTop: 8, ...tableWrap, background: '#fff' }}>
              <table style={tblStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Name</th>
                    <th style={thStyle}>Phone</th>
                    <th style={thStyle}>Wanted</th>
                    <th style={thStyle}>Cruise</th>
                    <th style={thStyle}>Reason</th>
                    <th style={thStyle}>Closed</th>
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recentlyClosed.map(row => {
                    const td = cellFor(row);
                    return (
                    <Fragment key={row.id}>
                    <tr>
                      <td style={{ ...td, fontWeight: 600, color: NAVY }}>
                        {row.full_name}
                        {sourceLine(row) && (
                          <div style={{ fontSize: '0.7rem', fontWeight: 500, color: 'var(--color-ink-muted)', marginTop: 2 }}>{sourceLine(row)}</div>
                        )}
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{row.phone || '—'}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{dateCell(row)}</td>
                      <td style={td}>{CRUISE_LABELS[row.cruise_preference] || row.cruise_preference || '—'}</td>
                      <td style={td}>{chip(reasonLabel(row.close_reason), reasonChipStyle(row.close_reason))}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--color-ink-muted)', fontSize: '0.74rem' }} title={new Date(row.closed_at).toLocaleString('en-IN')}>
                        {staffLabel(row.closed_by)} · {relTime(row.closed_at)}
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{renderActions(row)}</td>
                    </tr>
                    {noteRow(row, 7)}
                    </Fragment>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ padding: '8px 12px', fontSize: '0.72rem', color: 'var(--color-ink-faint)', borderTop: '1px solid var(--color-border-subtle)' }}>
                Leads closed in the last {RECENT_CLOSE_DAYS} days. "Date passed" ones may still be worth a call — wrong date, changed plans. Older closed leads are under the Closed filter.
              </div>
            </div>
          )}
        </div>
      )}

      {editing && (
        <EditEnquiryModal
          row={editing.row}
          reopenTo={editing.reopenTo}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {closing && (
        <CloseReasonModal
          row={closing}
          onClose={() => setClosing(null)}
          onConfirm={(reason) => closeWithReason(closing, reason)}
        />
      )}
    </div>
  );
}
